import { Component, EventEmitter, Input, Output } from "@angular/core";
import {
  GraphicsInfo,
  GraphicsInfoMediaField,
  GraphicsInfoMediaFit,
  GraphicsInfoMediaType,
  GraphicsInfoScaleField,
  GraphicsInfoTextField,
} from "app/js/src/domain/graphics-info";

/**
 * Clipping Studio–only editor for MAM graphic instance parameters.
 * Binds directly to {@link GraphicsInfo} (no scheduler icn-graphics-* components).
 */
@Component({
  selector: "evrideo-clipping-studio-mam-graphic-params-editor",
  templateUrl: "./clipping-studio-mam-graphic-params-editor.component.html",
  styleUrls: ["./clipping-studio-mam-graphic-params-editor.component.scss"],
})
export class ClippingStudioMamGraphicParamsEditorComponent {
  @Input({ required: true }) graphicsInfo!: GraphicsInfo;
  @Output() paramsChange = new EventEmitter<void>();

  readonly MediaFit = GraphicsInfoMediaFit;
  readonly MediaTypeEnum = GraphicsInfoMediaType;

  /** Same bounds/step as {@link GraphicsScaleComponent} / `icn-graphics-scale`. */
  readonly gfxScaleMin = 0;
  readonly gfxScaleMax = 100;
  readonly gfxScaleStep = 0.5;

  readonly durationMinSec = 0.1;
  readonly durationMaxSec = 86400;
  readonly durationStepSec = 0.1;

  notify(): void {
    this.paramsChange.emit();
  }

  getTextFields(): GraphicsInfoTextField[] {
    const f = this.graphicsInfo?.fields;
    if (!f?.length) return [];
    return f.filter((x) => x instanceof GraphicsInfoTextField) as GraphicsInfoTextField[];
  }

  showDurationField(): boolean {
    const g = this.graphicsInfo;
    return !!g && isFinite(g.startLoopMS) && isFinite(g.endLoopMS);
  }

  durationSeconds(): number {
    return (this.graphicsInfo?.durationMS ?? 0) / 1000;
  }

  onDurationSecondsChange(raw: number): void {
    let sec = Number(raw);
    if (!Number.isFinite(sec)) {
      return;
    }
    sec = Math.min(this.durationMaxSec, Math.max(this.durationMinSec, sec));
    this.graphicsInfo.durationMS = Math.round(sec * 1000);
    this.notify();
  }

  getMediaFields(): GraphicsInfoMediaField[] {
    const f = this.graphicsInfo?.fields;
    if (!f?.length) return [];
    return f.filter((x) => x instanceof GraphicsInfoMediaField) as GraphicsInfoMediaField[];
  }

  getScaleField(): GraphicsInfoScaleField | null {
    const f = this.graphicsInfo?.fields;
    if (!f?.length) return null;
    return (
      (f.find((x) => x instanceof GraphicsInfoScaleField) as GraphicsInfoScaleField) ??
      null
    );
  }

  /**
   * Mirrors {@link GraphicsScaleComponent#ngOnInit}: UI percent from stored multiplier.
   */
  graphicsScaleSelectedValue(field: GraphicsInfoScaleField): number {
    return parseFloat((field.value * 100).toFixed(3));
  }

  /**
   * Mirrors {@link GraphicsScaleComponent#onValueChange}.
   */
  onGraphicsScaleValueChange(field: GraphicsInfoScaleField, newVal: number): void {
    field.value = newVal / 100;
    this.notify();
  }

  onGfxScaleSliderInput(field: GraphicsInfoScaleField, raw: string): void {
    const v = parseFloat(raw);
    if (!Number.isFinite(v)) {
      return;
    }
    field.value = v / 100;
    this.notify();
  }

  onZLayerChange(z: number): void {
    this.graphicsInfo.zLayer = z;
    this.notify();
  }

  mediaTypeTitle(t: GraphicsInfoMediaType): string {
    switch (t) {
      case GraphicsInfoMediaType.None:
        return "None";
      case GraphicsInfoMediaType.MediaId:
        return "Media ID";
      case GraphicsInfoMediaType.DynamicOption:
        return "Dynamic";
      case GraphicsInfoMediaType.Link:
        return "Link";
      default:
        return String(t);
    }
  }

  fitMediaTitle(v: GraphicsInfoMediaFit): string {
    switch (v) {
      case GraphicsInfoMediaFit.IgnoreAspect:
        return "Ignore Aspect";
      case GraphicsInfoMediaFit.LetterBox:
        return "Letterbox";
      case GraphicsInfoMediaFit.StretchToCover:
        return "Stretch to Cover";
      default:
        return String(v);
    }
  }
}
